// ============================================================================
// AccueilTeamView — onglet « Mon equipe » de l'accueil (L10, cablage API existante)
// ============================================================================
// Consomme GET /api/mon-equipe + /sante + CRUD reminders. Objectifs via E1b
// (useTeams mutations). Gating M-150 deja fait en amont (AccueilView masque
// l'onglet sur 403). M-154 : roster sans salaire ni comment ; sante lazy-load.

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  EmptyState, StatusBadge, GsSegmented, SkeletonCard, QueryError, ConfirmDialog,
  GS_COMPONENTS,
} from '@gs/ui';
import type { GsSegmentedOption } from '@gs/ui';
import {
  Users, Target, BellRing, Plus, Trash2, Check, Undo2, HeartPulse,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  composePersonRoleSubtitle,
  derivePersonRole,
  personRoleI18nKey,
} from '../../lib/org/derivePersonRole';
import {
  useMonEquipe, useSanteEquipe, useToggleReminder, useDeleteReminder,
  type RosterMember, type Reminder, type MonEquipeObjective,
} from '../../hooks/useMonEquipe';
import { useUpdateObjective, useDeleteObjective, teamErrorCode } from '../../hooks/useTeams';
import { NewTeamObjectiveDialog } from '../../components/mon-equipe/NewTeamObjectiveDialog';
import { NewReminderDialog } from '../../components/mon-equipe/NewReminderDialog';
import { TeamHealthBoard } from '../../components/mon-equipe/TeamHealthBoard';
import {
  BusinessEntityCard,
  BusinessEntityCardMetaItem,
  BusinessEntityCardMetaRow,
} from '../../components/BusinessEntityCard';
import { AccueilTeamKpis } from './AccueilTeamKpis';

type TeamSection = 'roster' | 'sante';

const NEXT_STATUS: Record<MonEquipeObjective['status'], MonEquipeObjective['status']> = {
  pending: 'in_progress',
  in_progress: 'done',
  done: 'pending',
};

function RosterMemberCard({ member, teamId }: { member: RosterMember; teamId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  function openProfile() {
    const params = new URLSearchParams({ tab: 'gestion', from: 'mon-equipe', fromTeam: teamId });
    navigate(`/profile/${encodeURIComponent(member.email)}?${params.toString()}`);
  }

  const role = derivePersonRole({
    systemCRole: member.systemCRole,
    isTeamDirector: member.isDirector,
  });
  const subtitle = composePersonRoleSubtitle(
    role ? t(personRoleI18nKey(role)) : null,
    member.jobTitle,
  ) ?? undefined;

  return (
    <BusinessEntityCard
      title={member.fullName ?? member.email}
      subtitle={subtitle}
      badge={
        member.submitted ? (
          <StatusBadge variant="success">{t('gest:monEquipe.roster.upToDate')}</StatusBadge>
        ) : (
          <StatusBadge variant="neutral">{t('gest:monEquipe.roster.awaiting')}</StatusBadge>
        )
      }
      activateLabel={t('gest:accueil.teamView.roster.openProfile', {
        name: member.fullName ?? member.email,
      })}
      onActivate={openProfile}
    >
      {member.submitted && member.workloadScore != null && member.energyScore != null ? (
        <BusinessEntityCardMetaRow>
          <BusinessEntityCardMetaItem label={t('gest:accueil.teamView.roster.pulse')}>
            {t('gest:monEquipe.roster.scores', {
              workload: member.workloadScore,
              energy: member.energyScore,
            })}
          </BusinessEntityCardMetaItem>
        </BusinessEntityCardMetaRow>
      ) : null}
    </BusinessEntityCard>
  );
}

function ObjectiveItem({
  objective, teamId, canWrite, onError,
}: {
  objective: MonEquipeObjective;
  teamId: string;
  canWrite: boolean;
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateObjective(teamId);
  const remove = useDeleteObjective(teamId);
  const variant = objective.status === 'done' ? 'success' : objective.status === 'in_progress' ? 'warning' : 'neutral';

  return (
    <li className="flex items-center gap-3 border-b border-slate-100 py-3 last:border-0 dark:border-slate-700">
      <div className="min-w-0 flex-1">
        <p className="truncate text-field text-slate-900 dark:text-slate-100">{objective.title}</p>
        {objective.targetDate ? (
          <p className="text-meta text-slate-500 dark:text-slate-400">
            {t('gest:monEquipe.objectives.due', { date: objective.targetDate })}
          </p>
        ) : null}
      </div>
      <StatusBadge variant={variant}>{t(`gest:monEquipe.objectives.status.${objective.status}`)}</StatusBadge>
      {canWrite ? (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            aria-label={t('gest:monEquipe.objectives.advance')}
            disabled={update.isPending}
            onClick={() =>
              update.mutate(
                {
                  id: objective.id,
                  payload: {
                    title: objective.title,
                    status: NEXT_STATUS[objective.status],
                    targetDate: objective.targetDate,
                  },
                },
                { onError: (err) => onError(teamErrorCode(err)) },
              )
            }
            className="touch-manipulation rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
          >
            {objective.status === 'done'
              ? <Undo2 size={16} aria-hidden="true" />
              : <Check size={16} aria-hidden="true" />}
          </button>
          <button
            type="button"
            aria-label={t('gest:monEquipe.objectives.delete')}
            disabled={remove.isPending}
            onClick={() => remove.mutate(objective.id, { onError: (err) => onError(teamErrorCode(err)) })}
            className="touch-manipulation rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
          >
            <Trash2 size={16} aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </li>
  );
}

function ReminderItem({ reminder, onError }: { reminder: Reminder; onError: (code: string) => void }) {
  const { t } = useTranslation();
  const toggle = useToggleReminder();
  const remove = useDeleteReminder();
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <li className="flex items-center gap-3 border-b border-slate-100 py-3 last:border-0 dark:border-slate-700">
        <button
          type="button"
          aria-label={reminder.done ? t('gest:monEquipe.reminders.reopen') : t('gest:monEquipe.reminders.complete')}
          disabled={toggle.isPending}
          onClick={() =>
            toggle.mutate({ id: reminder.id, done: !reminder.done }, { onError: (err) => onError(teamErrorCode(err)) })
          }
          className="touch-manipulation rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
        >
          {reminder.done ? <Undo2 size={16} aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}
        </button>
        <div className="min-w-0 flex-1">
          <p className={`truncate text-field ${reminder.done ? 'text-slate-400 line-through dark:text-slate-500' : 'text-slate-900 dark:text-slate-100'}`}>
            {reminder.title}
          </p>
          {reminder.body ? (
            <p className="truncate text-meta text-slate-500 dark:text-slate-400">{reminder.body}</p>
          ) : null}
          {reminder.dueDate ? (
            <p className="text-meta text-slate-500 dark:text-slate-400">
              {t('gest:monEquipe.reminders.due', { date: reminder.dueDate })}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          aria-label={t('gest:monEquipe.reminders.delete')}
          disabled={remove.isPending}
          onClick={() => setConfirmOpen(true)}
          className="shrink-0 touch-manipulation rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
        >
          <Trash2 size={16} aria-hidden="true" />
        </button>
      </li>
      <ConfirmDialog
        open={confirmOpen}
        title={t('gest:accueil.teamView.reminders.confirmTitle')}
        message={t('gest:accueil.teamView.reminders.confirmMessage')}
        confirmLabel={t('gest:accueil.teamView.reminders.confirmConfirm')}
        cancelLabel={t('gest:accueil.teamView.reminders.confirmCancel')}
        variant="danger"
        isLoading={remove.isPending}
        onConfirm={() =>
          remove.mutate(reminder.id, {
            onSuccess: () => setConfirmOpen(false),
            onError: (err) => { setConfirmOpen(false); onError(teamErrorCode(err)); },
          })
        }
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}

export function AccueilTeamView({ enabled }: { enabled: boolean }) {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [section, setSection] = useState<TeamSection>('roster');
  const [objectiveOpen, setObjectiveOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const teamParam = searchParams.get('team');
  const q = useMonEquipe(teamParam, enabled);
  const data = q.data;
  const selectedTeam = data?.teams.find((team) => team.id === data.selectedTeamId) ?? null;
  const selectedTeamId = data?.selectedTeamId ?? null;

  const sante = useSanteEquipe(selectedTeamId, enabled && section === 'sante' && !!selectedTeamId);

  function selectTeam(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set('team', id);
    setSearchParams(next, { replace: true });
  }

  const sectionOptions: GsSegmentedOption[] = [
    { value: 'roster', label: t('gest:accueil.teamView.sections.roster') },
    { value: 'sante', label: t('gest:accueil.teamView.sections.health') },
  ];

  if (q.isLoading) {
    return (
      <div className="space-y-4">
        <SkeletonCard lines={2} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  if (q.isError) {
    return <QueryError message={t('gest:monEquipe.loadError')} onRetry={() => void q.refetch()} />;
  }

  if (!data) return null;

  const openReminders = data.reminders.filter((r) => !r.done).length;
  const submittedCount = data.roster.filter((m) => m.submitted).length;

  return (
    <div className="space-y-5">
      {actionError ? (
        <div
          role="alert"
          className="rounded-xl border-l-4 border-red-500 bg-red-50 p-4 text-field text-red-700 dark:bg-red-950 dark:text-red-300"
        >
          {t(`gest:monEquipe.errors.${actionError}`, { defaultValue: t('gest:monEquipe.errors.server') })}
        </div>
      ) : null}

      {data.teams.length > 1 ? (
        <GsSegmented
          options={data.teams.map((team) => ({ value: team.id, label: team.name }))}
          value={data.selectedTeamId ?? data.teams[0].id}
          onChange={selectTeam}
          ariaLabel={t('gest:monEquipe.teamSelector')}
          variant="scrollable"
        />
      ) : null}

      {!selectedTeam ? (
        <EmptyState
          icon={<Users size={48} aria-hidden="true" />}
          title={t('gest:monEquipe.empty.title')}
          description={t('gest:monEquipe.empty.description')}
        />
      ) : (
        <>
          <GsSegmented
            ariaLabel={t('gest:accueil.teamView.sections.toggleAria')}
            options={sectionOptions}
            value={section}
            onChange={(v) => setSection(v as TeamSection)}
            fullWidth
          />

          {section === 'roster' ? (
            <div className="space-y-5">
              <AccueilTeamKpis
                locale={i18n.language}
                headcount={data.roster.length}
                submittedCount={submittedCount}
                payroll={data.payroll}
                openReminders={openReminders}
              />

              <section className="space-y-3">
                <h2 className="text-head font-semibold text-slate-900 dark:text-slate-100">
                  {t('gest:monEquipe.roster.title')}
                </h2>
                {data.roster.length === 0 ? (
                  <EmptyState icon={<Users size={48} aria-hidden="true" />} title={t('gest:monEquipe.roster.empty')} />
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {data.roster.map((member) => (
                      <RosterMemberCard key={member.email} member={member} teamId={selectedTeam.id} />
                    ))}
                  </div>
                )}
              </section>

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <section className={`${GS_COMPONENTS.card} p-5`}>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <h2 className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100">
                      <Target size={18} aria-hidden="true" className="text-slate-500" />
                      {t('gest:monEquipe.objectives.title')}
                    </h2>
                    {data.viewer.canWriteObjectives ? (
                      <button
                        type="button"
                        onClick={() => setObjectiveOpen(true)}
                        className={`${GS_COMPONENTS.btnSecondary} touch-manipulation inline-flex min-h-11 items-center gap-1`}
                      >
                        <Plus size={16} aria-hidden="true" />
                        {t('gest:monEquipe.objectives.add')}
                      </button>
                    ) : null}
                  </div>
                  {data.objectives.length === 0 ? (
                    <p className="py-4 text-field text-slate-400 dark:text-slate-500">
                      {t('gest:monEquipe.objectives.empty')}
                    </p>
                  ) : (
                    <ul>
                      {data.objectives.map((objective) => (
                        <ObjectiveItem
                          key={objective.id}
                          objective={objective}
                          teamId={selectedTeam.id}
                          canWrite={!!data.viewer.canWriteObjectives}
                          onError={setActionError}
                        />
                      ))}
                    </ul>
                  )}
                </section>

                <section className={`${GS_COMPONENTS.card} p-5`}>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <h2 className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100">
                      <BellRing size={18} aria-hidden="true" className="text-slate-500" />
                      {t('gest:monEquipe.reminders.title')}
                    </h2>
                    <button
                      type="button"
                      onClick={() => setReminderOpen(true)}
                      className={`${GS_COMPONENTS.btnSecondary} touch-manipulation inline-flex min-h-11 items-center gap-1`}
                    >
                      <Plus size={16} aria-hidden="true" />
                      {t('gest:monEquipe.reminders.add')}
                    </button>
                  </div>
                  {data.reminders.length === 0 ? (
                    <p className="py-4 text-field text-slate-400 dark:text-slate-500">
                      {t('gest:monEquipe.reminders.empty')}
                    </p>
                  ) : (
                    <ul>
                      {data.reminders.map((reminder) => (
                        <ReminderItem key={reminder.id} reminder={reminder} onError={setActionError} />
                      ))}
                    </ul>
                  )}
                </section>
              </div>

              <NewTeamObjectiveDialog
                open={objectiveOpen}
                onClose={() => setObjectiveOpen(false)}
                onError={setActionError}
                teamId={selectedTeam.id}
              />
              <NewReminderDialog
                open={reminderOpen}
                onClose={() => setReminderOpen(false)}
                onError={setActionError}
              />
            </div>
          ) : (
            <div className="space-y-4">
              {sante.isLoading ? (
                <SkeletonCard lines={6} />
              ) : sante.isError ? (
                <QueryError message={t('gest:sante.loadError')} onRetry={() => void sante.refetch()} />
              ) : sante.data ? (
                <TeamHealthBoard
                  roster={sante.data.roster}
                  pulses={sante.data.pulses}
                  checkins={sante.data.checkins}
                />
              ) : (
                <EmptyState
                  icon={<HeartPulse size={48} aria-hidden="true" />}
                  title={t('gest:sante.empty.title')}
                  description={t('gest:sante.empty.description')}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
